import React, { Fragment } from "react";
import { connect } from "react-redux";
import withStyles from "isomorphic-style-loader/withStyles";
import isNil from "lodash.isnil";
import isEqual from "lodash.isequal";
import { EditorState, ContentState, RawDraftEntity } from "draft-js";
import {
  DraftailEditor,
  createEditorStateFromRaw,
  BLOCK_TYPE,
  INLINE_STYLE,
} from "draftail";
import { convertToHTML, convertFromHTML } from "draft-convert";
import * as draftJsStyles from "draft-js/dist/Draft.css";
import * as draftailStyles from "draftail/dist/draftail.css";
import * as s from "./styles.scss";
import ENTITY_TYPE_LINK from "./entities/link";
import {
  serviceCaller,
  withLoginRedirect,
  ServiceCallerProps,
} from "../../composition";

const exportConfig = {
  styleToHTML: (style: string) => {
    if (style === INLINE_STYLE.STRIKETHROUGH) {
      return <del />;
    }
    return null;
  },
  blockToHTML: () => {
    return null;
  },
  entityToHTML: (entity: RawDraftEntity, originalText: string) => {
    if (entity.type === ENTITY_TYPE_LINK.type) {
      return <a href={entity.data.url} />;
    }
    return originalText;
  },
};

const importConfig = {
  htmlToEntity: (nodeName, node, createEntity) => {
    if (nodeName === "a") {
      return createEntity(ENTITY_TYPE_LINK.type, "MUTABLE", {
        url: node.href,
        preview: node.text,
      });
    }
    return null;
  },
  htmlToStyle: (nodeName, _, currentStyle) => {
    if (nodeName === "del") {
      return currentStyle.add(INLINE_STYLE.STRIKETHROUGH);
    }
    return currentStyle;
  },
};

interface Props extends ServiceCallerProps {
  className?: string;
  readonly?: boolean;
  justScroll?: boolean;
  initialStyledContent?: string;
  placeholder?: string;
  onlySMS: boolean;
  onEditorChange: (content: string, styledContent: string) => void;
}

interface State {
  editorState: EditorState;
  editor: any;
}

class PostEditor extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      editorState: createEditorStateFromRaw(null),
      editor: null,
    };
  }

  // Wait until the component is mounted to render the Draft editor (Draftjs Issue when using SSR)
  // Ref: https://github.com/facebook/draft-js/issues/473#issuecomment-369833012
  componentDidMount() {
    let styledContent: ContentState = undefined;
    if (!isNil(this.props.initialStyledContent)) {
      styledContent = convertFromHTML(importConfig)(
        this.props.initialStyledContent
      );
    }

    this.setState({
      editor: DraftailEditor,
      editorState: isNil(styledContent)
        ? createEditorStateFromRaw(null)
        : EditorState.createWithContent(styledContent),
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (
      !isEqual(prevProps.initialStyledContent, this.props.initialStyledContent)
    ) {
      let styledContent: ContentState = undefined;
      if (!isNil(this.props.initialStyledContent)) {
        styledContent = convertFromHTML(importConfig)(
          this.props.initialStyledContent
        );
      }

      this.setState({
        editorState: isNil(styledContent)
          ? createEditorStateFromRaw(null)
          : EditorState.createWithContent(styledContent),
      });
    }
    if (!isEqual(this.props.onlySMS, prevProps.onlySMS)) {
      let currentContent: ContentState = this.state.editorState.getCurrentContent();
      const plainContent = currentContent.getPlainText();
      currentContent = convertFromHTML(importConfig)(plainContent);
      this.setState({
        editorState: EditorState.createWithContent(currentContent),
      });
    }
  }

  onEditorStateChange = (editorState: EditorState) => {
    this.setState({ editorState });

    const currentContent: ContentState = editorState.getCurrentContent();
    const plainContent = currentContent.getPlainText();
    const styledContent = convertToHTML(exportConfig)(currentContent);

    this.props.onEditorChange(plainContent, styledContent);
  };

  render() {
    const Editor = this.state.editor;
    const {
      className,
      readonly,
      placeholder,
      onlySMS,
      justScroll,
    } = this.props;

    let editorProps = {
      ariaDescribedBy: "postContent",
      className: className,
      readOnly: readonly,
      placeholder: placeholder,
      editorState: this.state.editorState,
      onChange: justScroll ? null : this.onEditorStateChange,
      inlineStyles: [],
      entityTypes: [],
      blockTypes: [],
      showUndoControl: false,
      showRedoControl: false,
      spellCheck: true,
    };

    if (onlySMS) {
      editorProps["topToolbar"] = null;
    } else {
      editorProps = {
        ...editorProps,
        blockTypes: [
          {
            type: BLOCK_TYPE.UNORDERED_LIST_ITEM,
            icon: <i className="fas fa-list-ul" />,
          },
          {
            type: BLOCK_TYPE.ORDERED_LIST_ITEM,
            icon: <i className="fas fa-list-ol" />,
          },
        ],
        inlineStyles: [
          { type: INLINE_STYLE.BOLD, icon: <i className="fas fa-bold" /> },
          {
            type: INLINE_STYLE.ITALIC,
            icon: <i className="fas fa-italic" />,
          },
          {
            type: INLINE_STYLE.UNDERLINE,
            icon: <i className="fas fa-underline" />,
          },
          {
            type: INLINE_STYLE.STRIKETHROUGH,
            icon: <i className="fas fa-strikethrough" />,
          },
        ],
        entityTypes: [ENTITY_TYPE_LINK],
        showUndoControl: true,
        showRedoControl: true,
      };
    }
    return (
      <Fragment>
        {isNil(this.state.editor) ? null : <Editor {...editorProps} />}
      </Fragment>
    );
  }
}

export default serviceCaller(
  withLoginRedirect(
    connect(
      () => ({}),
      () => ({})
    )(withStyles(draftJsStyles, draftailStyles, s)(PostEditor))
  )
);
